import React, {useState, useCallback, useMemo, useEffect} from "react"
import PropTypes from "prop-types"
import {Paper, Box, useTheme} from "@mui/material"
import {useNavigate} from "react-router-dom"
import {ReactComponent as EditIcon} from "src/v2/assets/icons/lock-empty-icon.svg"
import {ReactComponent as TrashIcon} from "src/v2/assets/icons/trash-icon.svg"
import {ReactComponent as CopyIcon} from "src/v2/assets/icons/copy-icon.svg"
import {ReactComponent as PasteIcon} from "src/v2/assets/icons/paste-icon.svg"
import FormActions from "../../components/FormActions"
import DuplaConfirmacao from "../../../../components/DuplaConfirmacao/DuplaConfirmacao"
import {dispatchSnackbar} from "../../../../components/SnackBar/snackbar-bus"
import MotorEventosExtraordinariosService from "src/services/motor-eventos-extraordinarios-service"
import GridFormSheetPagination from "../../../../components/GridFormSheetPagination"
import ReactInputMask from "react-input-mask"
import TextField from "@mui/material/TextField"
import {
    mapEventosExtraordinariosToRows,
    mapRowsToEventosExtraordinariosPayload,
    mapSingleRowToPayload,
    hasEventoData,
    hasAllRequiredFields,
    isSameEventoRow,
    extractSingleEventoFromResponse,
} from "./map-eventos-extraordinarios"

const TABLE_DATA_DEFAULT = [{}]

const DateInlineCell = ({params, inlineHandlers}) => {
    const value = params?.value || ""
    const [localValue, setLocalValue] = useState(value)

    useEffect(() => {
        setLocalValue(value)
    }, [value])

    const handleChange = (event) => {
        setLocalValue(event.target.value)
    }

    const handleBlur = (event) => {
        const newValue = event.target.value
        inlineHandlers.onChangeValue(params.id, params.field, newValue)
        inlineHandlers.onBlurPersist(params.id, params.field, newValue, params.row)
    }

    const handleKeyDown = (event) => {
        const keysToStop = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"]

        if (keysToStop.includes(event.key)) {
            event.stopPropagation()
        }

        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "a") {
            event.stopPropagation()
        }
    }

    return (
        <ReactInputMask
            mask="99/99/9999"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            maskChar=""
        >
            {(inputProps) => (
                <TextField
                    {...inputProps}
                    variant="standard"
                    fullWidth
                    autoFocus={false}
                    onKeyDown={handleKeyDown}
                    InputProps={{
                        disableUnderline: true,
                        sx: {fontSize: "14px"},
                    }}
                />
            )}
        </ReactInputMask>
    )
}

DateInlineCell.propTypes = {
    params: PropTypes.object.isRequired,
    inlineHandlers: PropTypes.shape({
        onChangeValue: PropTypes.func.isRequired,
        onBlurPersist: PropTypes.func.isRequired,
    }).isRequired,
}

const EventosExtraordinariosPage = ({cetip, eventosProducao, setEventosProducao}) => {
    const service = useMemo(() => new MotorEventosExtraordinariosService(), [])
    const theme = useTheme()
    const navigate = useNavigate()

    const [loading, setLoading] = useState(false)
    const [tableData, setTableData] = useState(TABLE_DATA_DEFAULT)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [publishConfirmOpen, setPublishConfirmOpen] = useState(false)
    const [rowDeleteConfirm, setRowDeleteConfirm] = useState({
        open: false,
        row: null,
        resolver: null,
    })
    const [editarConfirmOpen, setEditarConfirmOpen] = useState(false)

    const handleClearTable = useCallback(() => {
        setTableData(TABLE_DATA_DEFAULT)
    }, [])

    const handleEditarClick = (e) => {
        e?.preventDefault()
        e?.stopPropagation()
        setEditarConfirmOpen(true)
    }

    const handleConfirmEditar = () => {
        setEditarConfirmOpen(false)
        handlePublish()
    }

    const handleCancelEditar = () => {
        setEditarConfirmOpen(false)
    }

    const DateMaskCell = useCallback(
        (params, inlineHandlers) => (
            <DateInlineCell params={params} inlineHandlers={inlineHandlers}/>
        ),
        []
    )

    const columnsOrder = useMemo(
        () => [
            "dataReferencia",
            "dataEvento",
            "dataLiquidacao",
            "tipoEvento",
            "valorPercentual",
            "valorFinanceiro",
            "fonte",
        ],
        []
    )

    const columnsConfig = useMemo(
        () => [
            {
                field: "dataReferencia",
                headerName: "Data Referência",
                renderInlineCell: DateMaskCell,
            },
            {
                field: "dataEvento",
                headerName: "Data Evento",
                renderInlineCell: DateMaskCell,
            },
            {
                field: "dataLiquidacao",
                headerName: "Data de Liquidação",
                renderInlineCell: DateMaskCell,
            },
            {
                field: "tipoEvento",
                headerName: "Tipo de Evento",
            },
            {
                field: "valorPercentual",
                headerName: "Valor Percentual",
            },
            {
                field: "valorFinanceiro",
                headerName: "Valor Financeiro",
            },
            {
                field: "fonte",
                headerName: "Fonte",
            },
        ],
        [DateMaskCell]
    )

    useEffect(() => {
        if (!cetip) return

        loadEventosExtraordinarios()
    }, [cetip, loadEventosExtraordinarios])

    const loadEventosExtraordinarios = useCallback(async () => {
        setLoading(true)

        try {
            const response = await service.getEventosExtraordinarios(cetip)
            const body = response?.body

            if (body && body.length > 0) {
                setEventosProducao(true)
                setTableData(mapEventosExtraordinariosToRows(body))
            } else {
                await loadTemporario()
            }
        } catch (_error) {
            await loadTemporario()
        } finally {
            setLoading(false)
        }
    }, [cetip, service, setEventosProducao, loadTemporario])

    const loadTemporario = useCallback(async () => {
        try {
            const response = await service.getTemporarioEventosExtraordinarios(cetip)

            const eventos =
                response?.body?.EventosExtraordinarios ||
                response?.data?.EventosExtraordinarios

            if (eventos && eventos.length > 0) {
                setEventosProducao(false)
                setTableData(mapEventosExtraordinariosToRows(eventos))
            }
        } catch (_error) {
            // Sem dados temporários.
        }
    }, [cetip, service, setEventosProducao])

    const handleSave = async () => {
        const payload = mapRowsToEventosExtraordinariosPayload(tableData, cetip)

        try {
            setLoading(true)

            const saveResponse =
                await service.createCadastroTemporarioEventosExtraordinarios(
                    cetip,
                    {EventosExtraordinarios: payload}
                )

            const mensagem = saveResponse?.body?.mensagem ?? "Salvo com sucesso!"

            dispatchSnackbar(mensagem, "success")
        } catch (error) {
            const data =
                error?.response?.body ||
                error?.response?.data ||
                error?.response?.text

            const eventos = data?.EventosExtraordinarios

            if (Array.isArray(eventos)) {
                const mensagens = eventos.flatMap(item =>
                    Object.entries(item).flatMap(([campo, erros]) =>
                        erros.map(msg => `${campo} : ${msg}`)
                    )
                )

                dispatchSnackbar(mensagens.join(" | "), "error")
                return
            }

            dispatchSnackbar("Erro ao salvar", "error")
        } finally {
            setLoading(false)
        }
    }

    const handlePublish = async () => {
        setLoading(true)

        const rowsWithData = tableData.filter(row => hasEventoData(row))

        if (rowsWithData.length === 0) {
            try {
                await service.createCadastroEventosExtraordinarios(cetip, {
                    EventosExtraordinarios: [],
                })

                dispatchSnackbar("Publicado com sucesso.", "success")
                setEventosProducao(true)
            } catch (error) {
                const msg =
                    error?.response?.body?.detail ||
                    error?.response?.data?.detail ||
                    error?.message ||
                    "Falha ao publicar."

                dispatchSnackbar(msg, "error")
            } finally {
                setLoading(false)
                setPublishConfirmOpen(false)
            }

            return
        }

        let successCount = 0
        let errorCount = 0
        const updates = []

        try {
            for (const row of rowsWithData) {
                const serverID = row._serverID
                const payload = mapSingleRowToPayload(row, cetip)

                try {
                    if (!serverID) {
                        const response =
                            await service.createSingleEventoExtraordinario(
                                cetip,
                                payload
                            )

                        const created = extractSingleEventoFromResponse(response)
                        const createdId = created?.ID ?? created?.id ?? created?.Id

                        if (createdId && row.id) {
                            updates.push({
                                localId: row.id,
                                _serverID: createdId,
                            })
                        }
                    } else {
                        await service.updateCadastroEventoExtraordinario(
                            cetip,
                            serverID,
                            payload
                        )
                    }

                    successCount += 1
                } catch (error) {
                    errorCount += 1

                    const data = error?.response?.body || error?.response?.data
                    const detail = data?.detail || data?.message || data?.erro

                    if (detail) {
                        dispatchSnackbar(detail, "error")
                    }
                }
            }

            if (updates.length) {
                setTableData(prev =>
                    prev.map(row => {
                        const match = updates.find(u => u.localId === row.id)

                        return match
                            ? {
                                  ...row,
                                  _serverID: match._serverID,
                                  id: match._serverID,
                              }
                            : row
                    })
                )
            }

            if (errorCount === 0) {
                dispatchSnackbar(
                    eventosProducao
                        ? "Cadastro atualizado em produção com sucesso."
                        : "Cadastro publicado com sucesso.",
                    "success"
                )

                setEventosProducao(true)
            } else if (successCount > 0) {
                dispatchSnackbar(
                    eventosProducao
                        ? `Atualização parcial: ${successCount} evento(s) salvo(s) e ${errorCount} não salvo(s) por erro.`
                        : `Publicação parcial: ${successCount} evento(s) publicado(s) e ${errorCount} não salvo(s) por erro.`,
                    "warning"
                )

                setEventosProducao(true)
            } else {
                dispatchSnackbar(
                    eventosProducao
                        ? "Nenhuma alteração foi salva. Corrija os eventos com erro e tente novamente."
                        : "Nenhum evento foi publicado. Corrija os eventos com erro e tente novamente.",
                    "error"
                )
            }
        } catch (_error) {
            dispatchSnackbar(
                "Falha ao publicar eventos extraordinários.",
                "error"
            )
        } finally {
            setLoading(false)
            setPublishConfirmOpen(false)
        }
    }

    const handleDelete = useCallback(async () => {
        try {
            setLoading(true)

            if (!cetip) return

            if (eventosProducao) {
                await service.deleteCadastroEventosExtraordinarios(cetip)
            } else {
                await service.deleteCadastroTemporarioEventosExtraordinarios(cetip)
            }

            setTableData(TABLE_DATA_DEFAULT)
            setEventosProducao(false)

            dispatchSnackbar(
                eventosProducao
                    ? "Cadastro deletado com sucesso."
                    : "Cadastro temporário deletado com sucesso.",
                "success"
            )
        } catch (_error) {
            dispatchSnackbar("Erro ao deletar cadastro.", "error")
        } finally {
            setLoading(false)
            setDeleteConfirmOpen(false)
        }
    }, [cetip, eventosProducao, service, setEventosProducao])

    const handleDeleteRow = useCallback(async (row) => {
        if (!hasEventoData(row)) return true

        return new Promise((resolve) => {
            setRowDeleteConfirm({
                open: true,
                row,
                resolver: resolve,
            })
        })
    }, [])

    const handleConfirmDeleteRow = useCallback(async () => {
        const {row, resolver} = rowDeleteConfirm

        const finish = (result) => {
            resolver?.(result)

            setRowDeleteConfirm({
                open: false,
                row: null,
                resolver: null,
            })

            setLoading(false)
        }

        try {
            setLoading(true)

            if (row?._serverID && eventosProducao) {
                await service.deleteCadastroEventoExtraordinario(
                    cetip,
                    row._serverID
                )
            }

            finish(true)
        } catch (error) {
            const data = error?.response?.body || error?.response?.data

            const message =
                data?.detail ||
                data?.message ||
                data?.erro ||
                error?.message ||
                "Erro ao deletar evento."

            dispatchSnackbar(message, "error")
            finish(false)
        }
    }, [rowDeleteConfirm, cetip, eventosProducao, service])

    const handleCancelDeleteRow = useCallback(() => {
        const {resolver} = rowDeleteConfirm

        resolver?.(false)

        setRowDeleteConfirm({
            open: false,
            row: null,
            resolver: null,
        })
    }, [rowDeleteConfirm])

    const handlePersistRow = useCallback(
        async (newRow, oldRow) => {
            if (!eventosProducao || !newRow) return null
            const mergedRow = {...oldRow, ...newRow}
            const serverID = mergedRow._serverID
            const payload = mapSingleRowToPayload(mergedRow, cetip)

            try {
                if (!serverID) {
                    if (!hasAllRequiredFields(mergedRow)) return null
                    const response = await service.createSingleEventoExtraordinario(cetip, payload)
                    const created = extractSingleEventoFromResponse(response)
                    const createdId = created?.ID ?? created?.id ?? created?.Id
                    return createdId ? {_serverID: createdId, id: createdId} : null
                }
                if (oldRow && isSameEventoRow(mergedRow, oldRow)) {
                    return {_serverID: serverID}
                }
                await service.updateCadastroEventoExtraordinario(cetip, serverID, payload)
                return {_serverID: serverID}
            } catch (error) {
                dispatchSnackbar("Erro ao salvar evento.", "error")
                return null
            }
        },
        [cetip, eventosProducao, service]
    )

    return (
        <>
            <Paper sx={{pt: 3, pb: 2, pl: 4, pr: 4, mb: 8}}>
                <GridFormSheetPagination
                    rows={tableData}
                    onChange={setTableData}
                    columnsOrder={columnsOrder}
                    columnsConfig={columnsConfig}
                    onPersistRow={eventosProducao ? handlePersistRow : undefined}
                    onDeleteRow={handleDeleteRow}
                    alwaysEditable
                    pasteMode="append"
                    showClear
                    onClear={handleClearTable}
                    clearIcon={<TrashIcon />}
                    copyIcon={<CopyIcon />}
                    pasteIcon={<PasteIcon />}
                    rowHeight={50}
                    sx={{
                        marginTop: 3,
                        backgroundColor: theme.palette.background.primary,

                        ".MuiDataGrid-columnHeaders": {
                            backgroundColor: theme.palette.background.primary,
                            borderBottom: `1px solid ${theme.palette.input.borderColor.primary}`,
                            minHeight: "50px !important",
                            maxHeight: "50px !important",
                        },

                        ".MuiDataGrid-columnHeader": {
                            borderRight: `1px solid ${theme.palette.input.borderColor.primary}`,
                        },
                    }}
                />
            </Paper>

            <Box sx={{mx: {xxxs: -3, md: -3, lg: 0}, mt: 4}}>
                <FormActions
                    showBack
                    onBack={() => navigate(-1)}
                    onDelete={() => setDeleteConfirmOpen(true)}
                    deleteOpen={deleteConfirmOpen}
                    setDeleteOpen={setDeleteConfirmOpen}
                    onConfirmDelete={handleDelete}
                    showSave={!eventosProducao}
                    onSave={!loading ? handleSave : undefined}
                    onPublish={
                        eventosProducao
                            ? handleEditarClick
                            : () => setPublishConfirmOpen(true)
                    }
                    publishOpen={publishConfirmOpen}
                    setPublishOpen={setPublishConfirmOpen}
                    onConfirmPublish={handlePublish}
                    publishLabel={eventosProducao ? "EDITAR" : "PUBLICAR"}
                    publishIcon={eventosProducao ? <TrashIcon /> : <EditIcon />}
                    theme={theme}
                />
            </Box>

            <DuplaConfirmacao
                open={rowDeleteConfirm.open}
                cancel={handleCancelDeleteRow}
                confirm={handleConfirmDeleteRow}
                action="excluir"
                description="Confirma a exclusão deste Evento Extraordinário?"
                title="Excluir Evento Extraordinário"
            />

            <DuplaConfirmacao
                open={editarConfirmOpen}
                cancel={handleCancelEditar}
                confirm={handleConfirmEditar}
                title="Editar Eventos em Produção"
                description="
                    Certifique-se de que todas as alterações necessárias foram feitas,
                    já que este é um registro em produção.
                "
            />
        </>
    )
}

EventosExtraordinariosPage.propTypes = {
    cetip: PropTypes.string,
    eventosProducao: PropTypes.bool,
    setEventosProducao: PropTypes.func,
}

export default EventosExtraordinariosPage